import { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, AlertTriangle, Lock } from "lucide-react";

export default function TemplateSetup({ api, template, onUploaded }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.xlsx?$/)) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${api}/template`, fd);
      toast.success(`Template saved · ${res.data.item_count} items detected`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) doUpload(e.dataTransfer.files[0]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <Card className="lg:col-span-7 border-stone-200 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-2xl text-stone-900">
            {template?.exists ? "Replace master template" : "Upload master template"}
          </CardTitle>
          <CardDescription className="font-body text-stone-500">
            Item names should be in <span className="font-medium text-stone-900">Column B</span>.
            Quantities will be written into <span className="font-medium text-stone-900">Column C</span>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            data-testid="template-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`dropzone cursor-pointer border-2 border-dashed rounded-xl p-10 md:p-14 flex flex-col items-center justify-center text-center ${
              dragActive
                ? "drag-active border-amber-600 bg-amber-50/50"
                : "border-stone-300 bg-white hover:border-amber-500 hover:bg-amber-50/30"
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <UploadCloud className="w-7 h-7 text-amber-700" />
            </div>
            <p className="font-heading font-semibold text-stone-900 text-lg">
              Drop your template .xlsx here
            </p>
            <p className="font-body text-sm text-stone-500 mt-1">or click to browse</p>
            <input
              ref={inputRef}
              data-testid="template-file-input"
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])}
            />
            <Button
              type="button"
              data-testid="template-upload-btn"
              disabled={uploading}
              className="mt-6 rounded-full bg-amber-700 hover:bg-amber-800 text-white px-6 active:scale-95 transition-colors duration-200"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              {uploading ? "Uploading..." : template?.exists ? "Replace template" : "Choose file"}
            </Button>
          </div>

          <div className="mt-5 flex items-start gap-2 text-stone-500 font-body text-sm">
            <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Your template is stored permanently. Every future conversion writes into a fresh
              copy — the original stays untouched.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-5 border-stone-200 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-stone-900 flex items-center gap-2">
            {template?.exists ? (
              <>
                <FileCheck2 className="w-5 h-5 text-emerald-600" /> Current template
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-700" /> No template yet
              </>
            )}
          </CardTitle>
          <CardDescription className="font-body text-stone-500">
            {template?.exists
              ? "Items detected from Column B of your uploaded workbook."
              : "Upload one to unlock the Convert PO tab."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {template?.exists ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span
                  data-testid="template-filename"
                  className="font-body text-sm text-stone-900 truncate max-w-[180px]"
                  title={template.filename}
                >
                  {template.filename}
                </span>
                <Badge
                  data-testid="template-item-count"
                  className="bg-stone-100 text-stone-700 border border-stone-200 hover:bg-stone-100"
                >
                  {template.item_count} items
                </Badge>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100 bg-stone-50/50">
                {(template.items || []).map((name, i) => (
                  <div
                    key={i}
                    data-testid={`template-item-${i}`}
                    className="px-4 py-2.5 font-body text-sm text-stone-800 flex items-center justify-between"
                  >
                    <span className="truncate mr-3">{name}</span>
                    <span className="text-stone-400 text-xs">#{i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="font-body text-sm text-stone-500">
              Once uploaded, we&apos;ll show a preview of the item names we found here.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
